import { apiFetch } from '@/shared/api/client';
import type { AutorizacionEliminacionItem } from '@/shared/api/controllers/autorizacionEliminaciones';
import type { AutorizacionDescuentosItem } from '@/shared/api/controllers/autorizacionDescuentos';
import type { AutorizacionesInventarioItem } from '@/shared/api/controllers/autorizacionesInventario';

/**
 * Backend API wrappers for the Aprobaciones module. Every mutating call
 * returns a discriminated outcome so the page can branch on business-rule
 * rejections without a second round-trip (same shape as
 * `eliminarVenta` in controllers/ventas.ts).
 */

export async function listPendingEliminaciones(): Promise<AutorizacionEliminacionItem[]> {
  const res = await apiFetch<{ items: AutorizacionEliminacionItem[] }>(
    '/api/aprobaciones/eliminaciones',
  );
  return res.items;
}

/**
 * Per-row financial preview the server attaches to a pending discount
 * approval so the UI can show "what's already paid" + "what the venta
 * total will be after this approval lands" before the admin commits.
 * `overpaymentRisk` is true when projected < paid (would put the venta
 * into overpayment territory).
 */
export type DescuentoApprovalSummary = {
  moneda: 'ARS' | 'MXN' | 'USD';
  montoActualTotal: number;
  montoPagado: number;
  montoProyectadoTotal: number;
  overpaymentRisk: boolean;
};

export type AutorizacionDescuentosItemWithSummary = AutorizacionDescuentosItem & {
  summary?: DescuentoApprovalSummary;
};

export async function listPendingDescuentos(): Promise<AutorizacionDescuentosItemWithSummary[]> {
  const res = await apiFetch<{ items: AutorizacionDescuentosItemWithSummary[] }>(
    '/api/aprobaciones/descuentos',
  );
  return res.items;
}

// --------------------------------------------------------------------------
// Eliminaciones
// --------------------------------------------------------------------------

export type AprobarEliminacionResponse =
  | {
      /** Solicitud de un cobro: se anuló sólo ese cobro. */
      outcome: 'approved';
      alcance: 'cobro';
      autorizacionId: string;
      operacionId: string;
      comprobanteId: string;
      nroFactura: string | null;
    }
  | {
      outcome: 'approved';
      alcance: 'venta';
      autorizacionId: string;
      operacionId: string;
      anulados: {
        detalles: number;
        descuentos: number;
        pagos: number;
        comprobantes: number;
        autDescuentos: number;
        autInventario: number;
      };
    }
  | { outcome: 'blocked_status'; message: string };

export async function aprobarEliminacion(
  autorizacionId: string,
): Promise<AprobarEliminacionResponse> {
  return apiFetch<AprobarEliminacionResponse>(
    `/api/aprobaciones/eliminaciones/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export type RechazarEliminacionResponse =
  | { outcome: 'rejected'; autorizacionId: string }
  | { outcome: 'blocked_status'; message: string };

export async function rechazarEliminacion(
  autorizacionId: string,
): Promise<RechazarEliminacionResponse> {
  return apiFetch<RechazarEliminacionResponse>(
    `/api/aprobaciones/eliminaciones/${autorizacionId}/rechazar`,
    { method: 'POST' },
  );
}

// --------------------------------------------------------------------------
// Descuentos
// --------------------------------------------------------------------------

export type AprobarDescuentoResponse =
  | {
      outcome: 'approved';
      autorizacionId: string;
      operacionId: string;
      statusRO?: string;
      totals?: {
        subtotal: number;
        iva21: number;
        iva105: number;
        total: number;
      } | null;
    }
  | { outcome: 'blocked_status'; message: string }
  /**
   * Hard block when approving would drop the venta total below the
   * already-collected amount. The admin must either reject with a
   * smaller discount (`suggestedMaxPct` is the largest % that keeps
   * total ≥ paid; null when even 0% would still overpay) or cancel
   * the autorización entirely.
   */
  | {
      outcome: 'overpayment_risk';
      message: string;
      currentTotal: number;
      projectedTotal: number;
      paidSum: number;
      proposedDiscountPct: number;
      suggestedMaxPct: number | null;
    };

export async function aprobarDescuento(
  autorizacionId: string,
): Promise<AprobarDescuentoResponse> {
  return apiFetch<AprobarDescuentoResponse>(
    `/api/aprobaciones/descuentos/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export type RechazarDescuentoResponse =
  | {
      outcome: 'rejected_replaced';
      autorizacionId: string;
      operacionId: string;
      detalleId: string;
      nuevoDescuento: number;
      totals: {
        subtotal: number;
        iva21: number;
        iva105: number;
        total: number;
      } | null;
      statusRO?: string;
    }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'blocked_same_value'; message: string }
  /**
   * Same shape as the approve-side overpayment block. Fires when the
   * replacement-% the admin entered would drop the venta total below
   * the already-collected amount.
   */
  | {
      outcome: 'overpayment_risk';
      message: string;
      currentTotal: number;
      projectedTotal: number;
      paidSum: number;
      proposedDiscountPct: number;
      suggestedMaxPct: number | null;
    };

export async function rechazarDescuento(
  autorizacionId: string,
  nuevoDescuento: number,
): Promise<RechazarDescuentoResponse> {
  return apiFetch<RechazarDescuentoResponse>(
    `/api/aprobaciones/descuentos/${autorizacionId}/rechazar`,
    { method: 'POST', body: { nuevoDescuento } },
  );
}

/** Live "what-if" — does applying `descuento` as the replacement %
 *  on this approval's line tip the venta into usado territory?
 *  Used by the Rechazar dialog to warn the admin before they
 *  commit. AR / non-Nova MX always returns `{ wouldBeUsado: false }`. */
export async function previewDiscountUsado(
  autorizacionId: number,
  descuento: number,
): Promise<{ wouldBeUsado: boolean }> {
  return apiFetch<{ wouldBeUsado: boolean }>(
    `/api/aprobaciones/descuentos/${autorizacionId}/preview-usado?descuento=${encodeURIComponent(String(descuento))}`,
  );
}

// --------------------------------------------------------------------------
// Inventario (I7 — SR/PP auth triage)
// --------------------------------------------------------------------------

export async function listPendingInventarioAutorizaciones(): Promise<
  AutorizacionesInventarioItem[]
> {
  const res = await apiFetch<{ items: AutorizacionesInventarioItem[] }>(
    '/api/aprobaciones/inventario',
  );
  return res.items;
}

export type AprobarInventarioAutorizacionResponse =
  | { outcome: 'approved'; autorizacionId: string }
  | { outcome: 'blocked_status'; message: string };

export async function aprobarInventarioAutorizacion(
  autorizacionId: string,
): Promise<AprobarInventarioAutorizacionResponse> {
  return apiFetch<AprobarInventarioAutorizacionResponse>(
    `/api/aprobaciones/inventario/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export type RechazarInventarioAutorizacionResponse =
  | { outcome: 'rejected'; autorizacionId: string }
  | { outcome: 'blocked_status'; message: string };

export async function rechazarInventarioAutorizacion(
  autorizacionId: string,
): Promise<RechazarInventarioAutorizacionResponse> {
  return apiFetch<RechazarInventarioAutorizacionResponse>(
    `/api/aprobaciones/inventario/${autorizacionId}/rechazar`,
    { method: 'POST' },
  );
}

// --------------------------------------------------------------------------
// Facturación % (MX-only)
// --------------------------------------------------------------------------

export type FacturaPorcentajeItem = {
  id: number;
  salesHeaderId: number;
  venta: {
    sharepointId: number | null;
    cliente: string | null;
    totalCobrado: number | null;
    totalCotizacion: number | null;
    moneda: 'ARS' | 'MXN' | 'USD' | null;
  } | null;
  porcentajeSolicitado: number;
  porcentajeAnterior: number | null;
  umbral: number;
  motivo: string | null;
  solicitanteUsuarioApp: string | null;
  createdAt: string;
  /** MX Nova snapshot — true when applying the requested porcentaje
   *  would put the venta below the cost+commission+IVA threshold. */
  wasUsado: boolean;
};

export async function listPendingFacturaPorcentaje(): Promise<
  FacturaPorcentajeItem[]
> {
  const res = await apiFetch<{ items: FacturaPorcentajeItem[] }>(
    '/api/aprobaciones/factura-porcentaje',
  );
  return res.items;
}

export type AprobarFacturaPorcentajeResponse = {
  outcome: 'approved';
  autorizacionId: number;
  porcentajeAplicado: number;
};

export async function aprobarFacturaPorcentaje(
  autorizacionId: number,
): Promise<AprobarFacturaPorcentajeResponse> {
  return apiFetch<AprobarFacturaPorcentajeResponse>(
    `/api/aprobaciones/factura-porcentaje/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export type RechazarFacturaPorcentajeResponse = {
  outcome: 'rejected';
  autorizacionId: number;
  porcentajeAplicado: number | null;
};

export async function rechazarFacturaPorcentaje(
  autorizacionId: number,
  porcentajeReemplazo?: number,
): Promise<RechazarFacturaPorcentajeResponse> {
  return apiFetch<RechazarFacturaPorcentajeResponse>(
    `/api/aprobaciones/factura-porcentaje/${autorizacionId}/rechazar`,
    {
      method: 'POST',
      body:
        porcentajeReemplazo != null ? { porcentajeReemplazo } : undefined,
    },
  );
}

// --------------------------------------------------------------------------
// Cambio de horno (article swap)
// --------------------------------------------------------------------------

export type CambioHornoItem = {
  id: number;
  salesHeaderId: number;
  venta: {
    sharepointId: number | null;
    cliente: string | null;
    moneda: 'ARS' | 'MXN' | 'USD' | null;
  } | null;
  hornoAnterior: string | null;
  hornoNuevo: string | null;
  totalAnterior: number;
  totalNuevo: number;
  /** Discount % applied to the new horno (0 = precio de lista). */
  descuentoPct: number;
  delta: number;
  motivo: string | null;
  solicitanteUsuarioApp: string | null;
  createdAt: string;
};

export async function listPendingCambiosHorno(): Promise<CambioHornoItem[]> {
  const res = await apiFetch<{ items: CambioHornoItem[] }>(
    '/api/aprobaciones/cambio-horno',
  );
  return res.items;
}

export type AprobarCambioHornoResponse =
  | { outcome: 'approved'; autorizacionId: number; nextStatus: string; totalCobrado: number }
  | { outcome: 'blocked_status'; message: string }
  | { outcome: 'price_changed'; message: string }
  | { outcome: 'stock_insuficiente'; message: string }
  | { outcome: 'swap_failed'; message: string };

export async function aprobarCambioHorno(
  autorizacionId: number,
): Promise<AprobarCambioHornoResponse> {
  return apiFetch<AprobarCambioHornoResponse>(
    `/api/aprobaciones/cambio-horno/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export type RechazarCambioHornoResponse = { outcome: 'rejected'; autorizacionId: number };

export async function rechazarCambioHorno(
  autorizacionId: number,
): Promise<RechazarCambioHornoResponse> {
  return apiFetch<RechazarCambioHornoResponse>(
    `/api/aprobaciones/cambio-horno/${autorizacionId}/rechazar`,
    { method: 'POST' },
  );
}


// --------------------------------------------------------------------------
// Facturación Previa (AR-only) — emitir la factura antes del cobro
// --------------------------------------------------------------------------

export type FacturacionPreviaItem = {
  id: number;
  receiptId: number;
  salesHeaderId: number;
  venta: {
    sharepointId: number | null;
    nroCotizacion: string | null;
    cliente: string | null;
    total: number | null;
    moneda: 'ARS' | 'MXN' | 'USD' | null;
    status: string | null;
  } | null;
  comprobante: {
    sharepointId: number | null;
    medioPago: string | null;
    monto: number | null;
    pesos: number | null;
    status: string | null;
    fecha: string | null;
  } | null;
  montoSnapshot: number | null;
  /** La venta todavía no reservó stock: la factura saldría contra unidades
   *  que se le pueden vender a otro. Se le muestra al aprobador. */
  sinReservaStock: boolean;
  motivo: string | null;
  solicitanteUsuarioApp: string | null;
  createdAt: string;
};

export async function listPendingFacturacionPrevia(): Promise<
  FacturacionPreviaItem[]
> {
  const res = await apiFetch<{ items: FacturacionPreviaItem[] }>(
    '/api/aprobaciones/facturacion-previa',
  );
  return res.items;
}

export type ResolverFacturacionPreviaResponse = {
  ok: true;
  outcome: 'approved' | 'rejected';
};

export async function aprobarFacturacionPrevia(
  autorizacionId: number,
): Promise<ResolverFacturacionPreviaResponse> {
  return apiFetch<ResolverFacturacionPreviaResponse>(
    `/api/aprobaciones/facturacion-previa/${autorizacionId}/aprobar`,
    { method: 'POST' },
  );
}

export async function rechazarFacturacionPrevia(
  autorizacionId: number,
  motivo: string,
): Promise<ResolverFacturacionPreviaResponse> {
  return apiFetch<ResolverFacturacionPreviaResponse>(
    `/api/aprobaciones/facturacion-previa/${autorizacionId}/rechazar`,
    { method: 'POST', body: { motivo } },
  );
}
